/**
 * Redaction for the mutation `variables` attached to Bugsnag breadcrumbs.
 *
 * Mutation payloads here routinely carry user-authored content — ticket
 * comments, support-form field values (some flagged `sensitive` by the API),
 * search terms — and `File` objects for attachments. None of it may reach a
 * third-party tracker, and the `File` / large-payload cases also cost us the
 * breadcrumb entirely because Bugsnag has to serialize metadata.
 *
 * So we keep only the shape. Object keys are preserved (they name API fields,
 * not user input) and every value is replaced with a type marker unless its
 * key is on the identifier allowlist below.
 */

/**
 * Keys whose scalar values are record identifiers or server-defined enums,
 * never free text. Anything absent from this set is redacted, so new mutation
 * fields fail closed.
 */
const SAFE_VARIABLE_KEYS = new Set([
  // Identifiers
  'articleID',
  'assetId',
  'caseId',
  'contactGuid',
  'contactId',
  'formID',
  'formTitle',
  'id',
  'locationNum',
  'previousContactGuid',
  'resultId',
  'serviceEventId',
  'userGuid',
  // Enums and flags
  'eventType',
  'membership',
  'positive',
  'program',
  'promptType',
  'reasonCode',
  'resultContextType',
  'signal',
  'source',
  'status',
  'subscribe',
  'toggleOn',
  'type',
]);

/** Caps keep the breadcrumb small enough for Bugsnag to accept it. */
const MAX_DEPTH = 4;
const MAX_KEYS = 30;
const MAX_ARRAY_ITEMS = 10;
const MAX_STRING_LENGTH = 120;

export type RedactedVariables =
  | string
  | number
  | boolean
  | null
  | RedactedVariables[]
  | { [key: string]: RedactedVariables };

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const isScalar = (value: unknown): value is string | number | boolean | null =>
  value === null ||
  typeof value === 'boolean' ||
  typeof value === 'string' ||
  (typeof value === 'number' && Number.isFinite(value));

const keepScalar = (value: string | number | boolean | null) =>
  typeof value === 'string' && value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}...[truncated]`
    : value;

/** Type marker standing in for a value we refuse to send. */
const describe = (value: unknown): string => {
  if (value === null) return '[redacted null]';
  if (value === undefined) return '[redacted undefined]';
  if (Array.isArray(value)) return `[redacted array(${value.length})]`;

  if (typeof value === 'object') {
    // File, Blob, Date, Map and friends report a useful constructor name;
    // built-in names survive minification.
    const name = (value as object).constructor?.name;
    return `[redacted ${name || 'object'}]`;
  }

  return `[redacted ${typeof value}]`;
};

const redactValue = (value: unknown, depth: number): RedactedVariables => {
  // The depth cap also terminates circular references.
  if (Array.isArray(value)) {
    if (depth >= MAX_DEPTH) return describe(value);

    const items: RedactedVariables[] = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map(item => redactValue(item, depth + 1));

    return value.length > MAX_ARRAY_ITEMS
      ? [...items, `[${value.length - MAX_ARRAY_ITEMS} more]`]
      : items;
  }

  if (isPlainObject(value)) {
    if (depth >= MAX_DEPTH) return describe(value);

    const entries = Object.entries(value);
    const redacted: Record<string, RedactedVariables> = {};

    entries.slice(0, MAX_KEYS).forEach(([key, entryValue]) => {
      redacted[key] =
        SAFE_VARIABLE_KEYS.has(key) && isScalar(entryValue)
          ? keepScalar(entryValue)
          : redactValue(entryValue, depth + 1);
    });

    if (entries.length > MAX_KEYS) {
      redacted['[truncated]'] = `${entries.length - MAX_KEYS} more keys`;
    }

    return redacted;
  }

  return describe(value);
};

/**
 * Returns a Bugsnag-safe view of mutation variables, or `undefined` when there
 * is nothing to report (queries pass no variables at all).
 */
export const redactBreadcrumbVariables = (
  variables: unknown
): RedactedVariables | undefined =>
  variables === null || variables === undefined
    ? undefined
    : redactValue(variables, 0);
